class SoufitProduct {
  constructor(container) {
    this.container = container;
    const jsonEl = container.querySelector('[data-soufit-product-json]');
    this.data = jsonEl ? JSON.parse(jsonEl.textContent) : {};
    this.variants = this.data.variants || [];
    this.options = this.data.options || [];
    this.installmentsCount = parseInt(container.dataset.installments || 12);
    this.saborIdx = parseInt(container.dataset.saborIdx ?? -1);
    this.tamanhoIdx = parseInt(container.dataset.tamanhoIdx ?? -1);
    this.comboIdx = parseInt(container.dataset.comboIdx ?? -1);
    this.originalFirstImageSrc = null;

    this.selectedSabor = null;
    this.selectedTamanho = null;
    this.selectedCombo = null;

    // Detecta se o produto tem cards de combo
    this.hasCombo = container.querySelectorAll('.soufit-combo__card').length > 0;

    this.init();
  }

  init() {
    this.bindGallery();
    this.bindGalleryZoom();
    this.bindSabor();
    this.bindTamanho();
    this.bindCombo();
    this.bindStickyBar();
    this.bindCtaAjax();   // ← garante que nossa lógica de qty/variant chega ao carrinho
    this.selectDefaults();
  }

  /* ---- Habilita / desabilita o botão CTA (usado apenas para variante indisponível) ---- */
  setCtaEnabled(enabled) {
    const ctaBtn = this.container.querySelector('.soufit-cta');
    if (ctaBtn) ctaBtn.disabled = !enabled;
    const stickyBtn = this.container.querySelector('.soufit-sticky__btn');
    if (stickyBtn) stickyBtn.disabled = !enabled;
  }

  /* ---- Gallery ---- */
  bindGallery() {
    const scroll = this.container.querySelector('.soufit-product__gallery-scroll');
    const dots = this.container.querySelectorAll('.soufit-product__gallery-dot');

    // Guarda a imagem original para fallback quando variante não tem imagem
    const firstImg = scroll?.querySelector('.soufit-product__gallery-slide img');
    this.originalFirstImageSrc = firstImg?.src || null;

    if (!scroll) return;

    // ── Arrow buttons ─────────────────────────────────────────────
    const prevBtn = this.container.querySelector('.soufit-gallery__arrow--prev');
    const nextBtn = this.container.querySelector('.soufit-gallery__arrow--next');

    const getSlideWidth = () =>
      (scroll.querySelector('.soufit-product__gallery-slide')?.offsetWidth || scroll.offsetWidth) + 8;

    const updateArrows = () => {
      if (!prevBtn || !nextBtn) return;
      prevBtn.classList.toggle('soufit-gallery__arrow--hidden', scroll.scrollLeft < 4);
      nextBtn.classList.toggle('soufit-gallery__arrow--hidden',
        scroll.scrollLeft >= scroll.scrollWidth - scroll.offsetWidth - 4);
    };

    if (prevBtn) {
      prevBtn.classList.add('soufit-gallery__arrow--hidden'); // oculta no primeiro slide
      prevBtn.addEventListener('click', () => {
        scroll.scrollBy({ left: -getSlideWidth(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        scroll.scrollBy({ left: getSlideWidth(), behavior: 'smooth' });
      });
    }

    // Dots sync on scroll + arrow update
    scroll.addEventListener('scroll', () => {
      updateArrows();
      if (dots.length) {
        const index = Math.round(scroll.scrollLeft / getSlideWidth());
        dots.forEach((d, i) => d.classList.toggle('soufit-active', i === index));
      }
    }, { passive: true });

    // ── Mouse drag-to-scroll (desktop) ───────────────────────────
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let dragDistance = 0;

    scroll.addEventListener('mousedown', e => {
      isDragging = true;
      dragDistance = 0;
      startX = e.pageX - scroll.offsetLeft;
      startScrollLeft = scroll.scrollLeft;
      scroll.classList.add('is-dragging');
      e.preventDefault();
    });

    scroll.addEventListener('mousemove', e => {
      if (!isDragging) return;
      const x = e.pageX - scroll.offsetLeft;
      const walk = (x - startX) * 1.2;
      dragDistance = Math.abs(walk);
      scroll.scrollLeft = startScrollLeft - walk;
    });

    const stopDrag = () => {
      isDragging = false;
      scroll.classList.remove('is-dragging');
    };
    scroll.addEventListener('mouseup', stopDrag);
    scroll.addEventListener('mouseleave', stopDrag);

    // Prevent click after drag (> 8px movement = drag, not click)
    scroll.addEventListener('click', e => {
      if (dragDistance > 8) e.stopPropagation();
      dragDistance = 0;
    }, true);
  }

  /* ---- Sabor ---- */
  bindSabor() {
    const btns = this.container.querySelectorAll('.soufit-sabor__btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => b.classList.remove('soufit-active'));
        btn.classList.add('soufit-active');
        this.selectedSabor = btn.dataset.value;

        const selectedLabel = this.container.querySelector('.soufit-option__selected--sabor');
        if (selectedLabel) selectedLabel.textContent = this.selectedSabor;

        // Show/hide descriptions
        const allDescs = this.container.querySelectorAll('.soufit-sabor__desc-box');
        allDescs.forEach(d => d.classList.remove('soufit-visible'));
        const thisDesc = btn.nextElementSibling;
        if (thisDesc && thisDesc.classList.contains('soufit-sabor__desc-box')) {
          thisDesc.classList.add('soufit-visible');
        }

        this.updateComboPrices();
        this.resolveVariant();
      });
    });
  }

  /* ---- Tamanho ---- */
  bindTamanho() {
    const btns = this.container.querySelectorAll('.soufit-tamanho__btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => b.classList.remove('soufit-active'));
        btn.classList.add('soufit-active');
        this.selectedTamanho = btn.dataset.value;

        const selectedLabel = this.container.querySelector('.soufit-option__selected--tamanho');
        if (selectedLabel) selectedLabel.textContent = this.selectedTamanho;

        this.updateComboPrices();
        this.resolveVariant();
      });
    });
  }

  /* ---- Combo ---- */
  bindCombo() {
    const cards = this.container.querySelectorAll('.soufit-combo__card');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => {
          c.classList.remove('soufit-active');
          const r = c.querySelector('.soufit-combo__radio');
          if (r) r.checked = false;
        });
        card.classList.add('soufit-active');
        const radio = card.querySelector('.soufit-combo__radio');
        if (radio) radio.checked = true;
        this.selectedCombo = card.dataset.value;
        this.resolveVariant();
      });
    });
  }

  /* ---- Defaults ---- */
  selectDefaults() {
    // Sabor — seleciona o primeiro automaticamente
    const firstSabor = this.container.querySelector('.soufit-sabor__btn');
    if (firstSabor) firstSabor.click();

    // Tamanho — seleciona o último (maior) automaticamente
    const tamanhoBtns = this.container.querySelectorAll('.soufit-tamanho__btn');
    if (tamanhoBtns.length > 0) {
      tamanhoBtns[tamanhoBtns.length - 1].click();
    }

    // Combo — seleciona o primeiro card (1 Pote) como padrão
    const comboBtns = this.container.querySelectorAll('.soufit-combo__card');
    if (comboBtns.length > 0) {
      comboBtns[0].click();
    }

    // Garante que os preços por pote dos cards estejam atualizados após os defaults
    // (necessário para produtos sem sabor/tamanho que não acionam bindSabor/bindTamanho)
    this.updateComboPrices();
  }

  /* ---- Update combo card prices based on selected sabor/tamanho ---- */
  updateComboPrices() {
    const cards = this.container.querySelectorAll('.soufit-combo__card');
    cards.forEach(card => {
      const comboValue = card.dataset.value;
      const variant = this.findVariant(this.selectedSabor, this.selectedTamanho, comboValue);
      if (!variant) return;

      // Conta apenas números ligados a "Pote(s)" ou "Grátis" — ignora percentuais (5%, 10%)
      // "1 Pote" → 1 | "2 Potes + 1 Grátis" → 3 | "3 Potes com 10% desconto" → 3
      const potNums = (comboValue.match(/(\d+)\s*(?:Potes?|Grátis)/gi) || ['1 Pote']).map(m => parseInt(m));
      const totalPots = potNums.reduce((a, b) => a + b, 0) || 1;
      const pricePerUnit = Math.round(variant.price / totalPots);

      const unitEl = card.querySelector('.soufit-combo__price-unit');
      if (unitEl) unitEl.textContent = this.money(pricePerUnit);

      const totalOrigEl = card.querySelector('.soufit-combo__total-original');
      const totalEl = card.querySelector('.soufit-combo__total-discounted');
      const economyEl = card.querySelector('.soufit-combo__economy');
      const discountEl = card.querySelector('.soufit-combo__discount');
      const installEl = card.querySelector('.soufit-combo__installments');

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const economy = variant.compare_at_price - variant.price;
        const pct = Math.round(economy / variant.compare_at_price * 100);

        if (totalOrigEl) totalOrigEl.textContent = totalPots > 1 ? this.money(variant.compare_at_price) : '';
        if (totalEl) totalEl.textContent = totalPots > 1 ? 'Total ' + this.money(variant.price) : '';
        if (economyEl) { economyEl.textContent = 'economize ' + this.money(economy); economyEl.style.display = ''; }
        if (discountEl) { discountEl.textContent = pct + '% OFF'; discountEl.style.display = ''; }
      } else {
        if (totalOrigEl) totalOrigEl.textContent = '';
        if (totalEl) totalEl.textContent = totalPots > 1 ? 'Total ' + this.money(variant.price) : '';
        if (economyEl) economyEl.style.display = 'none';
        if (discountEl) discountEl.style.display = 'none';
      }

      if (installEl) {
        const installPrice = Math.round(variant.price / this.installmentsCount);
        installEl.textContent = this.installmentsCount + '× ' + this.money(installPrice);
      }
    });
  }

  /* ---- Find matching variant ---- */
  findVariant(sabor, tamanho, combo) {
    return this.variants.find(v => {
      return v.options.every((opt, i) => {
        if (i === this.saborIdx && sabor) return opt === sabor;
        if (i === this.tamanhoIdx && tamanho) return opt === tamanho;
        if (i === this.comboIdx && combo) return opt === combo;
        return true;
      });
    });
  }

  /* ---- Encontra a variante "1 Pote" base para ERP (recebe qty física correta) ---- */
  findBaseVariant(sabor, tamanho) {
    if (this.comboIdx < 0) return null;

    const matchesSaborTamanho = (v) =>
      v.options.every((o, i) => {
        if (i === this.saborIdx && sabor)  return o === sabor;
        if (i === this.tamanhoIdx && tamanho) return o === tamanho;
        return true;
      });

    // 1ª tentativa: regex "1 pote" (case-insensitive)
    const byRegex = this.variants.find(v => {
      const opt = v.options[this.comboIdx] || '';
      const m   = opt.match(/^(\d+)\s*potes?/i);
      return m && +m[1] === 1 && matchesSaborTamanho(v);
    });
    if (byRegex) return byRegex;

    // 2ª tentativa: menor número de potes entre os valores do combo
    // (garante robustez mesmo se o nome da opção variar)
    const comboVals = [...new Set(
      this.variants.map(v => v.options[this.comboIdx]).filter(Boolean)
    )];
    // Ordena pelo primeiro número encontrado na string ("2 Potes…" → 2)
    comboVals.sort((a, b) => {
      const na = parseInt(a.match(/\d+/) || [9999]);
      const nb = parseInt(b.match(/\d+/) || [9999]);
      return na - nb;
    });
    const firstVal = comboVals[0];
    if (!firstVal) return null;

    return this.variants.find(v =>
      v.options[this.comboIdx] === firstVal && matchesSaborTamanho(v)
    ) || null;
  }

  /* ---- Resolve current variant — atualiza form, imagem e sticky bar ---- */
  resolveVariant() {
    // Se o produto tem combo mas nenhum foi selecionado, mantém a variante padrão (não sobrescreve)
    if (this.hasCombo && !this.selectedCombo) return;

    const variant = this.findVariant(this.selectedSabor, this.selectedTamanho, this.selectedCombo);
    if (!variant) return;

    // Calcula a quantidade TOTAL de potes (pagos + grátis)
    // "2 Potes + 1 Grátis" → 3  |  "3 Potes + 2 Grátis" → 5  |  "1 Pote" → 1
    // A Shopify Function desconta o valor das unidades grátis sobre esse total.
    const comboVal = this.selectedCombo || '';
    const potNums = (comboVal.match(/(\d+)\s*(?:Potes?|Grátis)/gi) || ['1 Pote']).map(m => parseInt(m));
    const totalUnits = potNums.reduce((a, b) => a + b, 0) || 1;

    // Usa a variante "1 Pote" com a quantidade física total.
    // ERP recebe quantity correta. Desconto aplicado 100% server-side.
    if (this.comboIdx >= 0 && totalUnits > 0) {
      const baseVariant = this.findBaseVariant(this.selectedSabor, this.selectedTamanho);
      if (baseVariant) {
        this.container.querySelectorAll('[data-soufit-variant-id]').forEach(el => {
          el.value = baseVariant.id;
        });
        const qtyInput = this.container.querySelector('input[name="quantity"]');
        if (qtyInput) qtyInput.value = totalUnits;
      } else {
        // Fallback: variante combo original — mas quantity física sempre correta para o ERP
        this.container.querySelectorAll('[data-soufit-variant-id]').forEach(el => {
          el.value = variant.id;
        });
        const qtyInput = this.container.querySelector('input[name="quantity"]');
        if (qtyInput) qtyInput.value = totalUnits;
      }
    } else {
      // "1 Pote": usa normalmente com qty=1
      this.container.querySelectorAll('[data-soufit-variant-id]').forEach(el => {
        el.value = variant.id;
      });
      const qtyInput = this.container.querySelector('input[name="quantity"]');
      if (qtyInput) qtyInput.value = 1;
    }

    // Atualiza "Potes incluídos" no line item property do carrinho
    this.updatePotesProperty();

    // Atualiza imagem principal: usa a da variante se existir, senão usa a original do produto
    this.updateMainImageForVariant(variant);

    // Atualiza sticky bar (usa preço da variante combo para exibição correta)
    this.updateStickyBar(variant);

    // Habilita CTA conforme disponibilidade da variante
    if (this.hasCombo) {
      this.setCtaEnabled(variant.available);
    }
  }

  /* ---- Atualiza a propriedade "Potes incluídos" no carrinho ---- */
  updatePotesProperty() {
    const propEl = this.container.querySelector('[data-soufit-prop-potes]');
    if (!propEl) return;

    const combo = this.selectedCombo || '';
    // Conta potes: "2 Potes + 1 Grátis" → 3 | "3 Potes com 10% desconto" → 3 | "1 Pote" → 1
    const potNums = (combo.match(/(\d+)\s*(?:Potes?|Grátis)/gi) || ['1 Pote']).map(m => parseInt(m));
    const total = potNums.reduce((a, b) => a + b, 0) || 1;

    // Prefixo _ oculta a propriedade no carrinho do Shopify
    propEl.value = total > 1 ? total + ' potes' : '';
  }

  /* ---- Atualiza a imagem principal da galeria ---- */
  updateMainImageForVariant(variant) {
    const scroll = this.container.querySelector('.soufit-product__gallery-scroll');
    if (!scroll) return;

    if (variant && variant.featured_image) {
      // Variante tem imagem própria — atualiza o primeiro slide e volta ao início
      const firstImg = scroll.querySelector('.soufit-product__gallery-slide img');
      if (firstImg) {
        // Remove srcset para que o src seja respeitado pelo browser
        firstImg.removeAttribute('srcset');
        firstImg.removeAttribute('sizes');
        firstImg.src = variant.featured_image;
      }
      scroll.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      // Sem imagem na variante — rola para o slide correspondente ao índice do combo ativo
      const slides = scroll.querySelectorAll('.soufit-product__gallery-slide');
      if (slides.length <= 1) return;
      const cards = [...this.container.querySelectorAll('.soufit-combo__card')];
      const activeIdx = Math.max(0, cards.findIndex(c => c.classList.contains('soufit-active')));
      const targetIdx = Math.min(activeIdx, slides.length - 1);
      const slideWidth = (slides[0].offsetWidth || scroll.offsetWidth) + 8;
      scroll.scrollTo({ left: targetIdx * slideWidth, behavior: 'smooth' });
    }
  }

  /* ---- Sticky bar ---- */
  bindStickyBar() {
    const cta = this.container.querySelector('.soufit-cta');
    const sticky = this.container.querySelector('.soufit-sticky');
    if (!cta || !sticky) return;

    const observer = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle('soufit-sticky--visible', !entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(cta);

    const stickyBtn = sticky.querySelector('.soufit-sticky__btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = this.container.querySelector('[data-soufit-form]');
        if (form) form.submit();
      });
    }
  }

  updateStickyBar(variant) {
    const sticky = this.container.querySelector('.soufit-sticky');
    if (!sticky) return;

    const priceEl = sticky.querySelector('.soufit-sticky__price');
    if (priceEl) priceEl.textContent = this.money(variant.price);

    const oldEl = sticky.querySelector('.soufit-sticky__price-old');
    if (oldEl) {
      oldEl.textContent = (variant.compare_at_price > variant.price) ? this.money(variant.compare_at_price) : '';
    }

    const variantEl = sticky.querySelector('.soufit-sticky__variant');
    if (variantEl) {
      variantEl.textContent = variant.title !== 'Default Title' ? variant.title : '';
    }

    const btn = sticky.querySelector('.soufit-sticky__btn');
    if (btn) btn.disabled = !variant.available;
  }

  /* ---- Gallery Zoom / Lightbox ---- */
  bindGalleryZoom() {
    const slides = this.container.querySelectorAll('.soufit-product__gallery-slide');
    slides.forEach(slide => {
      slide.addEventListener('click', () => {
        const img = slide.querySelector('img');
        if (!img) return;
        // Tenta usar resolução maior substituindo o parâmetro de largura
        const src = (img.currentSrc || img.src).replace(/[?&]width=\d+/, match => match.replace(/\d+/, '1600'));
        this.openLightbox(src);
      });
    });
  }

  openLightbox(src) {
    // Evita duplicatas
    if (document.querySelector('.soufit-lightbox')) return;

    const overlay = document.createElement('div');
    overlay.className = 'soufit-lightbox';
    overlay.innerHTML = `
      <div class="soufit-lightbox__inner">
        <img src="${src}" class="soufit-lightbox__img" alt="">
        <button class="soufit-lightbox__close" aria-label="Fechar">✕</button>
      </div>
    `;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    const close = () => {
      overlay.classList.add('soufit-lightbox--closing');
      setTimeout(() => {
        overlay.remove();
        document.body.style.overflow = '';
      }, 220);
    };

    // Fecha ao clicar no fundo ou no X
    overlay.addEventListener('click', e => {
      if (e.target === overlay || e.target.classList.contains('soufit-lightbox__close')) close();
    });

    // Fecha com Esc
    const onKey = e => {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
    };
    document.addEventListener('keydown', onKey);

    // Anima entrada
    requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('soufit-lightbox--open')));
  }

  /* ---- AJAX Add to Cart + Animação + Drawer ---- */
  bindCtaAjax() {
    const form = this.container.querySelector('[data-soufit-form]');
    if (!form) return;

    const handleSubmit = async (e) => {
      e.preventDefault();

      // Encontra o botão CTA
      const btn = this.container.querySelector('.soufit-cta') ||
                  this.container.querySelector('[type="submit"]');
      if (!btn || btn.disabled) return;

      // Animação: estado loading
      const originalText = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add('soufit-cta--loading');
      btn.innerHTML = `<span class="soufit-cta__spinner"></span>`;

      try {
        // Coleta dados do formulário
        const formData = new FormData(form);
        const props = {};
        [...formData.entries()]
          .filter(([k]) => k.startsWith('properties['))
          .forEach(([k, v]) => {
            if (v) props[k.replace('properties[', '').replace(']', '')] = v;
          });

        // Coleta os section IDs dos cart-items-component (mesmo padrão do product-form.js do Horizon)
        const sectionIds = [...document.querySelectorAll('cart-items-component')]
          .map(el => el.dataset.sectionId)
          .filter(Boolean);

        const payload = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity') || 1),
        };
        if (Object.keys(props).length) payload.properties = props;
        if (sectionIds.length) payload.sections = sectionIds.join(',');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error('add_failed');
        const addResponse = await res.json();

        // Busca o carrinho completo para o evento
        const cartRes = await fetch('/cart.js');
        const fullCart = await cartRes.json();

        // Sucesso: ícone de check breve
        btn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

        // Despacha cart:update com sections renderizadas (Horizon atualiza cart-items-component via morphSection)
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: fullCart,
            sourceId: 'soufit-combo',
            data: {
              sections: addResponse.sections || {},
              itemCount: fullCart.item_count,
            },
          },
        }));

        // Abre o cart drawer
        const drawerEl = document.querySelector('cart-drawer-component');
        if (drawerEl) {
          if (typeof drawerEl.open === 'function') drawerEl.open();
          else if (typeof drawerEl.showDialog === 'function') drawerEl.showDialog();
        }

        // Restaura botão após 1.5s
        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.disabled = false;
          btn.classList.remove('soufit-cta--loading');
        }, 1500);

      } catch {
        // Fallback: submit normal
        btn.innerHTML = originalText;
        btn.disabled = false;
        btn.classList.remove('soufit-cta--loading');
        form.submit();
      }
    };

    form.addEventListener('submit', handleSubmit);

    // Sticky bar também usa AJAX
    const stickyBtn = this.container.querySelector('.soufit-sticky__btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    }
  }

  _getSectionsToRefresh() {
    // Retorna sections que precisam ser atualizadas no carrinho (header count, etc.)
    const sections = {};
    document.querySelectorAll('[data-section-id]').forEach(el => {
      const id = el.dataset.sectionId;
      if (id && (id.includes('cart') || id.includes('header'))) {
        sections[id] = true;
      }
    });
    return Object.keys(sections);
  }

  /* ---- Utility ---- */
  money(cents) {
    return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.soufit-product').forEach(el => new SoufitProduct(el));
});
